import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Grid } from "@mui/material"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faSpinnerThird } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api"
import { LatLng, getGeocode } from "use-places-autocomplete"
import { PlacesAutocomplete } from "./FormLocationAutoComplete"
import { IFormLocationPickerProps } from "./formlocationpicker.interface"

const LIBRARIES: ("places")[] = ["places"]

const FormLocationPicker: React.FC<IFormLocationPickerProps> = (props) => {
  const { handleChange, latLng, disableSelect, mode } = props

  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null)
  const [selected, setSelected] = useState<{ lat: number; lng: number } | null>(latLng ?? null)
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [selectedMapClick, setSelectedMapClick] = useState<string>("")

  const { isLoaded } = useLoadScript({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyCk4SCnuwj2yKZ_Z8CqIasGQIIiMh0lws8",
    libraries: LIBRARIES,
  })

  const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "300px",
    borderRadius: "4px",
  }

  const onLoad = useCallback((map: google.maps.Map | null) => setMap(map), [])

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude: lat, longitude: lng } = position.coords
          setUserLocation({ lat, lng })
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            console.warn("Location access blocked. Please enable it in your browser settings.")
          } else {
            console.error("Error getting geolocation:", error)
          }
        },
        { enableHighAccuracy: true }
      )
    } else {
      console.error("Geolocation not supported by browser.")
    }
  }, [])

  const center = useMemo(
    () => ({
      lat: mode === "view" && latLng ? latLng.lat : (userLocation?.lat ?? 62.39081947070066),
      lng: mode === "view" && latLng ? latLng.lng : (userLocation?.lng ?? 17.307067930699166),
    }),
    [userLocation, mode, latLng]
  )

  useEffect(() => {
    if (selected && map) {
      map.panTo(selected)
      const zoom = map.getZoom()
      if (zoom && zoom < 14) map.setZoom(14)
    }
  }, [selected, map])

  useEffect(() => {
    if (selected) {
      handleChange?.(JSON.stringify(selected))
    }
  }, [selected])

  const handleMapClick = async (event: google.maps.MapMouseEvent) => {
    const lat = event?.latLng?.lat()
    const lng = event?.latLng?.lng()
    if (!lat || !lng) return

    if (!disableSelect) {
      setSelected({ lat, lng })
    }

    try {
      const location = { lat, lng } as LatLng
      const results = await getGeocode({ location })
      const address = results[0]?.formatted_address
      if (address && !disableSelect) {
        setSelectedMapClick(address)
      }
    } catch (error) {
      console.error("Error fetching address:", error)
    }
  }

  if (!isLoaded) return <FontAwesomeIcon icon={faSpinnerThird as IconProp} spin />

  return (
    <Grid md={12} xs={12}>
      {!disableSelect && (
        <PlacesAutocomplete
          handleChange={(e: any) => handleChange?.(e)}
          setSelected={setSelected}
          selected={selected}
          selectedMapClick={selectedMapClick}
          {...props}
        />
      )}

      <GoogleMap
        zoom={10}
        center={center}
        mapContainerStyle={containerStyle}
        onLoad={onLoad}
        onClick={handleMapClick}
      >
        {selected && <Marker position={selected} />}
      </GoogleMap>
    </Grid>
  )
}

export default FormLocationPicker
